import { useEffect, useState } from "react";
import { Icon } from "./icons";
import { prefersReduce, usePrefersReducedMotion } from "./hooks";
import { useContent } from "./content";
import { SignalSpine } from "./SignalSpine";
import { APP_URL } from "./data";

function Kinetic({ words }: { words: string[] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (prefersReduce()) return;
    const id = setInterval(() => setI((p) => (p + 1) % words.length), 2100);
    return () => clearInterval(id);
  }, [words.length]);
  return (
    <span className="kin">
      <span className="kin__list" style={{ transform: `translateY(-${i * 1.26}em)`, transition: "transform .5s cubic-bezier(.6,0,.2,1)" }}>
        {words.map((w) => <span key={w} className="kin__w">{w}</span>)}
      </span>
    </span>
  );
}

const SRC = [
  { t: "TECHNICAL RIDER — TALE OF US", hdr: true },
  { t: "format: live electronic · 2 members", cls: "lbl" },
  { t: "DJ BOOTH", cls: "lbl" },
  { id: "cdj", t: "2× Pioneer CDJ-3000" },
  { id: "mix", t: "1× Pioneer DJM-A9 mixer" },
  { t: "MONITORING", cls: "lbl" },
  { id: "mon", t: "2× Function One F101 wedges" },
  { t: "STAGE NOTES", cls: "lbl" },
  { id: "exc", t: "absolutely no d&b PA on Main" },
];
const FIELDS = [
  { id: "cdj", beat: 1, label: "Media player", val: "Pioneer CDJ-3000", note: "×2", score: 0.98 },
  { id: "mix", beat: 2, label: "Mixer", val: "Pioneer DJM-A9", note: "", score: 0.96 },
  { id: "mon", beat: 3, label: "Booth monitors", val: "Function One F101", note: "×2", score: 0.74 },
  { id: "exc", beat: 4, label: "Stage exclusion", val: "No d&b PA · Main", note: "", score: 0.61 },
];
const TIERC: Record<string, string> = { ok: "var(--ok)", warn: "var(--warn)", bad: "var(--bad)" };
const tierOf = (s: number) => (s >= 0.85 ? "ok" : s >= 0.7 ? "warn" : "bad");

function LiveInstrument() {
  const reduce = usePrefersReducedMotion();
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    if (reduce) {
      setBeat(5);
      return;
    }
    const id = setInterval(() => setBeat((b) => (b + 1) % 6), 1500);
    return () => clearInterval(id);
  }, [reduce]);
  const shown = FIELDS.filter((f) => beat >= f.beat && beat !== 0);
  return (
    <div className="inst crop" data-reveal>
      <div className="inst__bar">
        <span className="inst__rec"><span className="d" />REC {String(Math.floor(beat * 15 / 60)).padStart(2, "0")}:{String((beat * 15) % 60).padStart(2, "0")}</span>
        <span className="inst__title">rider.pdf → structured</span>
        <span className="status-badge st-processing"><i />extracting</span>
      </div>
      <div className="inst__grid">
        <div className="inst__src">
          <div className="inst__lbl"><span>SOURCE · rider.pdf</span><span>p.1</span></div>
          <div className="inst__doc">
            {SRC.map((l, i) => {
              const f = "id" in l && l.id ? FIELDS.find((x) => x.id === l.id) : null;
              const hot = f && beat === f.beat, done = f && beat > f.beat;
              const cls = "srcline" + (l.hdr ? " hdr" : "") + (l.cls === "lbl" ? " lbl" : "") + (hot ? " is-hot" : "") + (done ? " done" : "");
              return <span key={i} className={cls}>{l.t}</span>;
            })}
          </div>
        </div>
        <div className="inst__col">
          <div className="inst__lbl"><span>EXTRACTED</span><span>{shown.length}/4</span></div>
          {FIELDS.map((f) => {
            const on = beat >= f.beat && beat !== 0, tier = tierOf(f.score);
            return (
              <div key={f.id} className={"xfield" + (on ? " on" : "")} style={{ ["--tier" as string]: TIERC[tier] }}>
                <span className="xfield__spine" />
                <div className="xfield__body"><div className="xfield__lbl">{f.label}</div><div className="xfield__val">{f.val} {f.note && <em>{f.note}</em>}</div></div>
                <div className="xfield__meterwrap"><span className="xfield__score">{f.score.toFixed(2)}</span><span className="meter"><span className="meter__fill" style={{ width: on ? Math.round(f.score * 100) + "%" : "0%" }} /></span></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function Hero({ motion }: { motion: boolean }) {
  const h = useContent().hero;
  return (
    <>
      <SignalSpine motion={motion} />
      <header className="v2hero cue" id="cue-input" data-cue="INPUT">
        <div className="v2-wrap">
          <div className="v2hero__top">
            <div>
              <span className="v2hero__badge v2-kick" style={{ animationDelay: "60ms" }}><span className="rec" />SIG.00 — {h.badge}</span>
              <h1 className="v2hero__title v2-kick" style={{ animationDelay: "140ms" }}>
                {h.titleLine1}<br />{h.titleLine2}<br />
                <span className="line2"><Kinetic words={h.kinetic} /></span>
              </h1>
              <p className="v2hero__sub v2-kick" style={{ animationDelay: "260ms" }}>{h.sub}</p>
              <div className="v2hero__cta v2-kick" style={{ animationDelay: "340ms" }}>
                <a className="btn btn--accent btn--lg" href={APP_URL + "/register"}>{h.ctaPrimary}<Icon name="arrowRight" size={16} className="btn__arrow" /></a>
                <a className="btn btn--ghost btn--lg" href="#cue-read">{h.ctaSecondary}</a>
              </div>
              <div className="v2hero__stat v2-kick" style={{ animationDelay: "420ms" }}>
                {h.stats.map((s, i) => <div key={i}><b>{s.n}</b><span>{s.l}</span></div>)}
              </div>
            </div>
            <div className="v2-kick" style={{ animationDelay: "300ms" }}><LiveInstrument /></div>
          </div>
        </div>
      </header>
    </>
  );
}
